import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Binoculars, Link, Plus, Trash, X } from "@phosphor-icons/react";
import { ChipSelector, LoadingSpinner, PlatformBadge } from "@/components/shared";
import { API } from "@/lib/api";

// ============= COMPETITOR INTEL SECTION =============
// ============= RESEARCH LIBRARY SECTION (replaces Competitor Intel) =============
const ResearchLibrary = () => {
  const [links, setLinks] = useState([]);
  const [newLink, setNewLink] = useState({
    url: "",
    title: "",
    notes: "",
    platform: "instagram",
    tags: "",
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const platforms = ["instagram", "tiktok", "linkedin", "youtube", "other"];

  const fetchLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/research/links`);
      setLinks(res.data.links);
    } catch (e) {
      console.error("Error fetching links:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const saveLink = async () => {
    if (!newLink.url || !newLink.title) return;
    try {
      await axios.post(`${API}/research/links`, {
        ...newLink,
        tags: newLink.tags.split(',').map(t => t.trim()).filter(Boolean)
      });
      setNewLink({ url: "", title: "", notes: "", platform: "instagram", tags: "" });
      setShowAddModal(false);
      fetchLinks();
    } catch (e) {
      console.error("Error saving link:", e);
    }
  };

  const deleteLink = async (id) => {
    try {
      await axios.delete(`${API}/research/links/${id}`);
      fetchLinks();
    } catch (e) {
      console.error("Error deleting link:", e);
    }
  };

  const filteredLinks = filter === "all" 
    ? links 
    : links.filter(l => l.platform === filter);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-zinc-900">Research Library</h2>
          <p className="text-zinc-500 mt-1">Save posts and content you like for inspiration</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          data-testid="add-research-link-btn"
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Save Link
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            filter === "all" ? "bg-[#E91E8C] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
          }`}
        >
          All ({links.length})
        </button>
        {platforms.map(p => {
          const count = links.filter(l => l.platform === p).length;
          if (count === 0) return null;
          return (
            <button
              key={p}
              onClick={() => setFilter(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                filter === p ? "bg-[#E91E8C] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
            >
              {p} ({count})
            </button>
          );
        })}
      </div>

      {/* Links Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredLinks.map((link) => (
          <div key={link.id} className="card p-4 card-hover group">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h4 className="font-semibold text-zinc-900 text-sm line-clamp-2">{link.title}</h4>
              <button
                onClick={() => deleteLink(link.id)}
                className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500 transition-all"
              >
                <Trash size={16} />
              </button>
            </div>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#E91E8C] hover:underline flex items-center gap-1 mb-2"
            >
              <Link size={12} /> View Original
            </a>
            {link.notes && (
              <p className="text-xs text-zinc-500 mb-2 line-clamp-3">{link.notes}</p>
            )}
            <div className="flex items-center gap-2">
              <PlatformBadge platform={link.platform} />
              {link.tags?.slice(0, 2).map(tag => (
                <span key={tag} className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {filteredLinks.length === 0 && (
        <div className="text-center py-12 text-zinc-500">
          <Binoculars size={48} className="mx-auto mb-3 text-zinc-300" />
          <p>No saved links yet. Click "Save Link" to add content for research.</p>
        </div>
      )}

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-zinc-900">Save Research Link</h3>
                <button onClick={() => setShowAddModal(false)} className="text-zinc-400 hover:text-zinc-600">
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">URL</label>
                  <input
                    type="url"
                    value={newLink.url}
                    onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                    className="input-field"
                    placeholder="https://instagram.com/p/..."
                    data-testid="research-url"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">Title</label>
                  <input
                    type="text"
                    value={newLink.title}
                    onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                    className="input-field"
                    placeholder="What's this about?"
                    data-testid="research-title"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">Notes (optional)</label>
                  <textarea
                    value={newLink.notes}
                    onChange={(e) => setNewLink({ ...newLink, notes: e.target.value })}
                    className="textarea-field h-20"
                    placeholder="Why did you save this? What do you like about it?"
                    data-testid="research-notes"
                  />
                </div>
                <ChipSelector
                  label="Platform"
                  options={platforms}
                  selected={newLink.platform}
                  onChange={(p) => setNewLink({ ...newLink, platform: p })}
                />
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">Tags (comma separated)</label>
                  <input
                    type="text"
                    value={newLink.tags}
                    onChange={(e) => setNewLink({ ...newLink, tags: e.target.value })}
                    className="input-field"
                    placeholder="hooks, reels, ai-content"
                    data-testid="research-tags"
                  />
                </div>
                <button onClick={saveLink} className="btn-primary w-full" data-testid="save-research-link">
                  Save to Library
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResearchLibrary;
